<template>
  <section id="experience" class="common-padding mb-20">
    <div class="flex flex-col">
      <h1
        id="experience-heading"
        v-html="experienceHeading"
        class="heading-1 text-start font-bold uppercase leading-none"
      ></h1>

      <div
        id="experience-text"
        class="mt-[5%] grid grid-cols-1 justify-end gap-4 text-flax-smoke-300 opacity-0 sm:grid-cols-12 md:column-gap lg:grid"
      >
        <p
          class="heading-6 col-span-full text-center text-flax-smoke-300/85 sm:col-span-4 sm:text-nowrap lg:col-start-2"
        >
          ( PROFESSIONAL JOURNEY )
        </p>
        <p
          class="heading-5 font-fancy col-span-full w-full text-balance sm:heading-4 sm:col-span-8 sm:font-semibold lg:col-span-7"
        >
          Building impactful products and scalable solutions across full-stack development, cloud infrastructure, and product engineering.
        </p>
      </div>
    </div>

    <div class="mt-12 flex flex-col gap-4 sm:gap-6 lg:mt-[10%]">
      <div
        v-for="(exp, index) in experiences"
        :key="index"
        @mouseenter="expandExperience(index)"
        @mouseleave="collapseExperience(index)"
        class="experience-card group relative overflow-hidden rounded-lg border border-flax-smoke-500/30 bg-flax-smoke-950/50 backdrop-blur-sm transition-all duration-300 hover:border-flax-smoke-400/50"
        :class="{ 'expanded': expandedExperiences[index], 'border-flax-smoke-300/40': index === 0 }"
      >
        <!-- Collapsed Header -->
        <div class="flex items-center justify-between p-4 sm:p-6">
          <div class="flex items-center gap-3 sm:gap-4">
            <div class="flex h-10 w-10 items-center justify-center rounded-lg bg-flax-smoke-800/50 sm:h-12 sm:w-12">
              <span class="text-lg font-bold text-flax-smoke-200 sm:text-xl">{{ exp.company.charAt(0) }}</span>
            </div>
            <div>
              <h3 class="flex items-center gap-2 text-base font-bold text-flax-smoke-100 sm:text-lg">
                {{ exp.title }}
                <span
                  v-if="index === 0"
                  class="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-flax-smoke-100/80"
                  title="Current"
                ></span>
              </h3>
              <p class="text-xs text-flax-smoke-400 sm:text-sm">
                {{ exp.company }}
              </p>
            </div>
          </div>
          <div class="flex items-center gap-3">
            <p class="hidden text-xs font-medium text-flax-smoke-500 sm:block sm:text-sm">
              {{ exp.period }}
            </p>
            <svg
              class="h-5 w-5 text-flax-smoke-400 transition-transform duration-300 sm:h-6 sm:w-6"
              :class="{ 'rotate-180': expandedExperiences[index] }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        <!-- Expanded Content -->
        <div
          v-show="expandedExperiences[index]"
          class="expanded-content overflow-hidden"
        >
          <div class="border-t border-flax-smoke-500/20 p-4 sm:p-6">
            <div class="mb-4">
              <p class="text-xs text-flax-smoke-400 sm:text-sm">
                {{ exp.location }}
              </p>
              <p class="mt-1 text-xs font-medium text-flax-smoke-500 sm:hidden sm:text-sm">
                {{ exp.period }}
              </p>
            </div>
            <ul class="space-y-2 sm:space-y-3">
              <li
                v-for="(achievement, i) in exp.achievements"
                :key="i"
                class="flex gap-2 text-flax-smoke-300 sm:gap-3"
              >
                <span class="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-flax-smoke-400"></span>
                <p class="text-sm leading-relaxed sm:text-base">{{ achievement }}</p>
              </li>
            </ul>
          </div>
        </div>

        <div
          class="absolute inset-0 -z-10 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        >
          <div class="absolute inset-0 bg-gradient-to-br from-flax-smoke-900/20 to-transparent"></div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { animateSplitText } from '@/animations';
  import { textSplitterIntoChar } from '@/functions';
  import { onBeforeMount, onMounted, ref } from 'vue';
  import { playSound } from '@/lib/sound-engine';
  import { uChatScrollButtonSound } from '@/lib/sounds/u-chat-scroll-button';

  const experienceHeading = ref('Experience /');
  const expandedExperiences = ref<Record<number, boolean>>({});

  const playClickSound = async () => {
    await playSound(uChatScrollButtonSound.dataUri, { volume: 0.4 });
  };

  const expandExperience = (index: number) => {
    expandedExperiences.value[index] = true;
    playClickSound();
  };

  const collapseExperience = (index: number) => {
    expandedExperiences.value[index] = false;
  };

  const experiences = [
    {
      title: 'AMTS Intern',
      company: 'Salesforce',
      location: 'Hyderabad - India',
      period: 'May 2026 – July 2026',
      achievements: [
        'Built an AI agent harness on data cloud platform MCP, enabling autonomous planning and execution of tool calls on their platform, significantly reducing the time users spent on repetitive manual workflows by 80%.',
        "Designed the High-Level Architecture for integrating and hosting the agent framework within Salesforce's core infrastructure, focusing on scalability and maintainability.",
        "Contributed directly to Salesforce's core repository, with 2 pull requests successfully merged into production, an uncommon achievement for an intern that demonstrated code quality and engineering ownership.",
      ],
    },
    {
      title: 'Founding Engineer',
      company: 'Pawgeny',
      location: 'Remote - US based',
      period: 'Nov 2025 – May 2026',
      achievements: [
        'Created the MVP in 3 weeks, enabling purchasing digital directives, verified caretaker assignment, and end-to-end commerce using Stripe with automated communication via Customer.io and product analytics via PostHog.',
        'Designed and shipped a secure QR-code smart dog tag system using a LCG to generate non-guessable identifiers, enabling user-controlled Lost Mode and verified data disclosure while preventing direct exposure of sensitive user information.',
        'Collaborated with founders and stakeholders (backed by Eleos Health) to drive critical product and technical decisions, while taking cross-functional ownership across product, engineering, and operations and coordinating with team members.',
      ],
    },
    {
      title: 'Full-stack Web Developer',
      company: 'Uncouple',
      location: 'Remote - US based',
      period: 'May 2025 – Nov 2025',
      achievements: [
        'Deployed a serverless file-processing microservice using AWS Lambda to scan and categorize client financial transactions accelerating financial analysis by 90%.',
        'Created a scalable DynamoDB data model for storing categorized transactions and processing metadata, enabling low-latency lookups and reliable handling of high-volume financial records.',
        'Implemented an end-to-end CI/CD pipeline using GitHub Actions to automate deployments across Vercel and Railway, including a secure AWS OIDC-based workflow to update Railway service variables, eliminating long-lived AWS credentials.',
      ],
    },
    {
      title: 'Full-stack Web Developer Intern',
      company: 'SpeedLegal',
      location: 'Remote - US based',
      period: 'Sept 2024 – Apr 2025',
      achievements: [
        'Brought into effect a recommendation feature that extracts clauses from legal contracts and allows users to input prompts for personalized, context-specific suggestions enhancing user experience by reducing search time by 30%.',
        'Developed an internal file lookup tool to enable the legal team to efficiently access and manage user-uploaded documents, improving document retrieval and workflow productivity by 45%.',
        'Leveraged AWS such as Lambda, API Gateway for API management achieving a monthly handling of 5,000+ requests.',
      ],
    },
  ];

  onBeforeMount(() => {
    experienceHeading.value = textSplitterIntoChar('Experience /', true);
  });

  onMounted(() => {
    animateSplitText(
      '#experience-heading .letters',
      '#experience-text',
      0.7,
      0.01,
      0,
    );
  });
</script>

<style scoped>
  .experience-card {
    position: relative;
  }

  .experience-card::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(135deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.5s;
  }

  .experience-card:hover::before {
    opacity: 1;
  }
</style>
